import { Button, Stack, Typography } from '@mui/material';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import { MouseEvent, ReactElement, useCallback, useMemo } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { getAddressTooltipLines } from 'src/components/SiteComponents/site-address-lines';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import type { Address } from 'src/graphql';
import { useCopyToClipboard, useOpenSite } from 'src/utils/hooks';

const ENTER_DELAY_MS = 400;
// long enough to travel from the title into the tooltip and reach the buttons
const LEAVE_DELAY_MS = 250;
const ACTION_ICON_SIZE = 14;
// equal halves, so the pair reads as one row of actions rather than two chips
const ACTION_BUTTON_SX = { flex: 1 };

interface SiteTitleTooltipProps {
  address: Address;
  children: ReactElement;
}

export const SiteTitleTooltip = ({ address, children }: SiteTitleTooltipProps) => {
  const { copyToClipboard } = useCopyToClipboard();
  const openSite = useOpenSite();

  const addressLines = useMemo(() => getAddressTooltipLines(address), [address]);

  const onCopy = useCallback(async () => {
    await copyToClipboard(addressLines.map(({ text }) => text).join('\n'));
  }, [addressLines, copyToClipboard]);

  const onOpen = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      openSite(event, address);
    },
    [address, openSite],
  );

  // the popper is portaled in the DOM, but React still bubbles its clicks up the
  // React tree to the card header's toggle button
  const stopBubbling = useCallback((event: MouseEvent) => {
    event.stopPropagation();
  }, []);

  return (
    <V2ThemeProvider>
      <Tooltip
        nonInteractable={false}
        enterDelay={ENTER_DELAY_MS}
        leaveDelay={LEAVE_DELAY_MS}
        placement="bottom-start"
        title={
          <Stack spacing={1} onClick={stopBubbling}>
            <Stack spacing={0.25}>
              {addressLines.map(({ id, text }) => (
                <Typography key={id} variant="body2" component="span">
                  {text}
                </Typography>
              ))}
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <Button
                size="small"
                fullWidth
                sx={ACTION_BUTTON_SX}
                onClick={onCopy}
                startIcon={
                  <DynamicIcon name="copy" library="lucide" size={ACTION_ICON_SIZE} />
                }
              >
                Copy
              </Button>
              <Button
                size="small"
                fullWidth
                sx={ACTION_BUTTON_SX}
                onClick={onOpen}
                startIcon={
                  <DynamicIcon
                    name="arrow-up-right"
                    library="lucide"
                    size={ACTION_ICON_SIZE}
                  />
                }
              >
                Open
              </Button>
            </Stack>
          </Stack>
        }
      >
        {children}
      </Tooltip>
    </V2ThemeProvider>
  );
};
